/**
 * AthleteOnboardingFlow
 * ─────────────────────────────────────────────────────────────────
 * Fires once, immediately after a brand-new user picks the "Athlete"
 * persona (see PersonaWelcomeModal.pick()). Five fast, real-data steps
 * — confirm date of birth, pick a sport, add a link, drop a quick photo
 * (which doubles as Media Hub asset + profile picture + FLYT ID photo),
 * then a recap with a confetti moment — each one writing straight to
 * the athlete's live profile so by the time the trial-forward paywall
 * hits, their FLYT ID already has real content on it instead of being
 * an empty shell.
 *
 * Every write uses the SAME endpoints the rest of the app uses to
 * edit a profile (PUT /me/athlete/profile, PUT /me/profile, the Media
 * Hub event/upload pair) — no new backend surface, so there's nothing
 * here that can drift out of sync with how editing works everywhere
 * else.
 *
 * iter866 · Jay: DOB collection lives HERE now, as this flow's own
 * first, required, un-skippable step — not a separate global overlay.
 * Calls the same `/auth/verify-age` endpoint DobGuard.js uses, so it's
 * the exact same authoritative source for Earn Program/payout age
 * eligibility either way. If the account already has a `dob` on file
 * (e.g. DobGuard already caught it, or this is a returning athlete
 * re-running onboarding), this step is skipped entirely — age is
 * decided once, never re-asked.
 *
 * Design: pure black/white liquid glass, matching PersonaWelcomeModal
 * (iter648) — no colored accents.
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import posthog from 'posthog-js';
import {
  ArrowRight, ArrowLeft, Check, Loader2,
  ImagePlus, Sparkles, Star, Cake,
  Youtube, Instagram, Music2, Globe,
} from 'lucide-react';
import { useUserAuth } from '../../contexts/UserAuthContext';
import { resolveImageUrl, shrinkImage } from '../ImageUpload';
import { haptic } from '../../lib/nativeBridge';
import { fireConfetti } from '../../lib/confetti';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOTAL_STEPS = 5;
// Same list SportIcon.jsx uses (kept in sync manually — small,
// stable list, not worth a shared-module extraction for one field).
const SPORTS = [
  'Football', 'Basketball', 'Baseball', 'Soccer', 'Volleyball', 'Softball',
  'Track & Field', 'Tennis', 'Golf', 'Wrestling', 'Lacrosse', 'Hockey',
  'Swimming', 'Gymnastics', 'Cross Country', 'Rowing', 'Other',
];
const track = (event, props = {}) => {
  try { posthog.capture?.(event, props); } catch { /* silent */ }
};
// One-tap platform buttons below the link input — each fills a single,
// obviously-placeholder link for that platform so an athlete who isn't
// ready to type a real handle can still leave onboarding with active
// icons on their FLYT ID, correctable anytime from Profile. Tapping is
// per-platform and toggles on/off (not an all-or-nothing "auto-fill").
const PLATFORM_ICONS = [
  { key: 'youtube', label: 'YouTube', Icon: Youtube, placeholder: 'https://youtube.com/@yourchannel' },
  { key: 'tiktok', label: 'TikTok', Icon: Music2, placeholder: 'https://tiktok.com/@yourhandle' },
  { key: 'instagram', label: 'Instagram', Icon: Instagram, placeholder: 'https://instagram.com/yourhandle' },
  { key: 'website', label: 'Website', Icon: Globe, placeholder: 'https://yourwebsite.com' },
];
const detectPlatform = (url) => {
  const u = url.toLowerCase();
  if (u.includes('tiktok.com')) return 'tiktok';
  if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
  if (u.includes('instagram.com')) return 'instagram';
  if (u.includes('twitter.com') || u.includes('x.com')) return 'twitter';
  if (u.includes('hudl.com')) return 'hudl';
  return 'website';
};
// The Media Hub is NOT a generic photo bucket — every real upload lives
// inside an event (game folder) and lands in `media_assets`. Writing
// straight into `athletes.photo_gallery` (tempting, since it's on the
// self-edit allowlist) is the WRONG field: that bucket is reserved for
// staff/AI-tagged content and bypasses the moderation gate that runs
// before anything reaches the public Network profile. So this goes
// through the exact same two-call path a real game-folder upload uses
// — create a lightweight personal event, then upload into it — which
// is what actually makes the photo show up as a real, tagged Media Hub
// asset instead of an unmoderated shortcut.
async function uploadQuickMediaHubPhoto(file) {
  const shrunk = await shrinkImage(file);
  const { data: event } = await axios.post(`${API}/me/media/events`, {
    title: 'My Photos',
    start_at: new Date().toISOString(),
  }, { withCredentials: true });
  const formData = new FormData();
  formData.append('file', shrunk);
  const { data } = await axios.post(`${API}/me/media/events/${event.id}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    withCredentials: true,
  });
  return data.url;
}
// Slide direction: 1 = advancing (slide in from right), -1 = going back
// (slide in from left) — mirrors the reference onboarding screens.
const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 48 : -48, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -48 : 48, opacity: 0 }),
};
const ProgressDots = ({ step }) => (
  <div className="flex items-center justify-center gap-1.5 pt-6">
    {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
      <span
        key={i}
        className={`h-1.5 rounded-full transition-all duration-300 ${
          i === step ? 'w-6 bg-white' : i < step ? 'w-1.5 bg-white/60' : 'w-1.5 bg-white/15'
        }`}
      />
    ))}
  </div>
);
const StepShell = ({ step, onBack, onSkip, skipLabel = 'Skip for now', children }) => (
  <div className="flex-1 flex flex-col min-h-0 px-6 pb-8" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}>
    <div className="flex items-center justify-between h-8">
      {step > 0 ? (
        <button type="button" onClick={onBack} className="text-white/50 hover:text-white transition p-1 -ml-1" aria-label="Back">
          <ArrowLeft className="w-5 h-5" />
        </button>
      ) : <span />}
      {onSkip && (
        <button type="button" onClick={onSkip} className="text-white/40 hover:text-white/70 text-xs font-bold uppercase tracking-widest transition">
          {skipLabel}
        </button>
      )}
    </div>
    <ProgressDots step={step} />
    <div className="flex-1 flex flex-col min-h-0 mt-8">{children}</div>
  </div>
);
const PrimaryButton = ({ onClick, busy, disabled, children, ...rest }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy || disabled}
    className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-full bg-white text-black font-black text-[13px] uppercase tracking-widest hover:bg-white/90 disabled:opacity-40 transition active:scale-[0.99]"
    {...rest}
  >
    {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <>{children}</>}
  </button>
);
export default function AthleteOnboardingFlow({ onDone }) {
  const { user, setUser, refreshUser } = useUserAuth();
  const navigate = useNavigate();
  // iter866 · Start at step 1 (Sport) instead of 0 (DOB) if this account
  // already has a dob on file — age is decided once, never re-asked.
  const [step, setStep] = useState(() => (user?.dob ? 1 : 0)); // 0 = DOB, 1-4 = the four screens, 5 = paywall
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = back — drives slide direction
  const [athlete, setAthlete] = useState(null); // latest athlete doc from the backend
  const [busy, setBusy] = useState(false);
  // Fires once, right when the recap screen ("Your FLYT ID is live.")
  // appears — that's the actual end-of-onboarding moment. iter753 · Jay: the old black/white
  // straight-down particles "looked like random water, not confetti" —
  // now fires the same colorful canvas-confetti burst + heavy→success
  // haptic pair as the Network Verified celebration.
  useEffect(() => {
    if (step !== 4) return undefined;
    haptic('heavy');
    const hapticTimer = setTimeout(() => haptic('success'), 380);
    const confettiTimer = setTimeout(fireConfetti, 350);
    return () => { clearTimeout(hapticTimer); clearTimeout(confettiTimer); };
  }, [step]);
  // Every "Next" gets a felt tick (medium impact); "Back"/"Skip" get a
  // lighter one so forward progress reads as the more decisive action.
  const goTo = (nextStep, { back = false } = {}) => {
    haptic(back ? 'light' : 'medium');
    setDirection(nextStep > step ? 1 : -1);
    setStep(nextStep);
  };
  // ── Step 0 · Date of birth ───────────────────────────────────────
  // iter866 · Jay: required, NOT skippable — the literal first thing
  // this flow shows. Same `/auth/verify-age` endpoint DobGuard.js
  // posts to, so it's the one authoritative source for age/Earn
  // Program eligibility either way.
  const [dob, setDob] = useState('');
  const [dobSaving, setDobSaving] = useState(false);
  const dobAge = useMemo(() => {
    if (!dob) return null;
    const d = new Date(dob);
    if (isNaN(d.getTime())) return null;
    const now = new Date();
    let a = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--;
    return a >= 0 && a <= 130 ? a : null;
  }, [dob]);
  const dobIsUnder13 = dobAge != null && dobAge < 13;
  const submitDob = async () => {
    if (dobSaving) return;
    if (!dob || dobAge == null) { toast.error('Please enter a valid date of birth.'); return; }
    if (dobIsUnder13) { toast.error('FLYT accounts are currently available to athletes ages 13 and older.'); return; }
    setDobSaving(true);
    try {
      const { data } = await axios.post(`${API}/auth/verify-age`, {
        dob,
        guardian_name: null,
        guardian_email: null,
        guardian_relationship: null,
      }, { withCredentials: true });
      if (data?.user) setUser(data.user);
      track('onboarding_dob_submitted', { age: dobAge });
      goTo(1);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || 'Could not save — try again.');
    } finally {
      setDobSaving(false);
    }
  };
  // ── Step 1 · Sport ───────────────────────────────────────────────
  // iter864 · Jay: nothing in onboarding should force a pick anymore —
  // skippable now, same as the Link and Photo steps below.
  const [sport, setSport] = useState('');
  const [sportSaving, setSportSaving] = useState(false);
  // Tapping a sport saves it AND advances immediately — no separate
  // "Continue" tap required. Guards on `sportSaving` so a double-tap
  // (or tapping a second sport mid-save) can't fire two writes.
  const selectSport = async (s) => {
    if (sportSaving) return;
    haptic('light');
    setSport(s);
    setSportSaving(true);
    try {
      const { data } = await axios.put(`${API}/me/athlete/profile`, {
        updates: { sport: s },
      }, { withCredentials: true });
      setAthlete(data);
      track('onboarding_sport_selected', { sport: s });
      goTo(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save — try again.');
    } finally {
      setSportSaving(false);
    }
  };
  // ── Step 2 · Link ────────────────────────────────────────────────
  const [linkUrl, setLinkUrl] = useState('');
  const [linkSaved, setLinkSaved] = useState(null); // { platform, url } once ANY link is saved
  const [platformLinks, setPlatformLinks] = useState({}); // { youtube: url, ... } — which icon toggles are on
  const saveSocialLinks = async (patch) => {
    setBusy(true);
    try {
      const current = athlete?.social_links || {};
      const { data } = await axios.put(`${API}/me/athlete/profile`, {
        updates: { social_links: { ...current, ...patch } },
      }, { withCredentials: true });
      setAthlete(data);
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save that link — try again.");
      return false;
    } finally {
      setBusy(false);
    }
  };
  const handleLinkContinue = async () => {
    const url = linkUrl.trim();
    if (!url) { goTo(3); return; }
    const withScheme = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    const platform = detectPlatform(withScheme);
    const ok = await saveSocialLinks({ [platform]: withScheme });
    if (ok) {
      setLinkSaved({ platform, url: withScheme });
      track('onboarding_link_added', { platform });
      goTo(3);
    }
  };
  // Per-platform icon tap — toggles that ONE platform's placeholder on
  // or off and saves immediately, so the FLYT ID updates in real time
  // as they tap through icons (independent of the manual paste input
  // and independent of each other — tapping YouTube doesn't touch
  // Instagram/TikTok/Website).
  const togglePlatform = async (key, placeholder) => {
    const isActive = !!platformLinks[key];
    const nextValue = isActive ? '' : placeholder;
    const ok = await saveSocialLinks({ [key]: nextValue });
    if (ok) {
      setPlatformLinks((prev) => ({ ...prev, [key]: nextValue }));
      if (nextValue) {
        setLinkSaved({ platform: key, url: nextValue });
        track('onboarding_link_platform_toggled', { platform: key });
        haptic('light');
      }
    }
  };
  // ── Step 3 · Quick photo — doubles as Media Hub asset + profile
  // picture + FLYT ID/Network photo, all from ONE upload. Previously
  // this was two separate steps (Media Hub photo, then a second
  // "profile picture" upload) — merged per Jay's request so dropping
  // one photo visibly does everything at once instead of asking twice.
  //
  // One upload, three writes, reusing the SAME url (no second file
  // upload needed):
  //   1. uploadQuickMediaHubPhoto → real, tagged Media Hub asset
  //   2. PUT /me/athlete/profile {photo: url} → Network tile/FLYT ID
  //      card (these render off `linked_athlete.photo`, NOT
  //      `user.picture` — this write is what makes the picture show
  //      up there, not just in the navbar)
  //   3. PUT /me/profile {picture: url} → account-wide avatar (navbar,
  //      comments, everywhere else that reads `user.picture`)
  //
  // NOTE: the profile-picture step used to call a plain /api/upload —
  // that endpoint is ADMIN-ONLY (Depends(get_current_admin)) and was
  // throwing "Invalid token" for every real athlete. Reusing the
  // Media Hub upload here (which is correctly gated on
  // get_current_public_user) fixes that bug as a side effect of the
  // merge, not just the merge itself.
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file.'); return; }
    setPhotoUploading(true);
    try {
      const url = await uploadQuickMediaHubPhoto(file);
      setPhotoUrl(url);
      const { data } = await axios.put(`${API}/me/athlete/profile`, {
        updates: { photo: url },
      }, { withCredentials: true });
      setAthlete(data);
      await axios.put(`${API}/me/profile`, { picture: url }, { withCredentials: true });
      await refreshUser();
      track('onboarding_photo_added');
      haptic('success');
      toast.success('Your photo is live', { description: 'Media Hub, profile picture, and your FLYT ID — all updated.' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed — try again.');
    } finally {
      setPhotoUploading(false);
    }
  };
  // ── Step 4 · Recap — bio, star rating, and info pills ────────────
  // All optional, all use the EXACT same fields the real FLYT ID
  // editor (IntegrateBuilder.js) already reads/writes — star_rating,
  // position/grade_year/jersey_number/hometown/school_name/
  // competition_level, flyt_link_accent_color — so this isn't a
  // separate onboarding-only concept, it's the same profile.
  const [bio, setBio] = useState('');
  const [bioFocused, setBioFocused] = useState(false);
  const [starRating, setStarRating] = useState(0);
  const [finishSaving, setFinishSaving] = useState(false);
  const saveFinalDetails = async () => {
    const updates = { flyt_link_accent_color: '#FFFFFF' }; // always white by default, matches the app's black/white identity
    const text = bio.trim();
    if (text) updates.bio = text;
    if (starRating > 0) updates.star_rating = starRating;
    setFinishSaving(true);
    try {
      const { data } = await axios.put(`${API}/me/athlete/profile`, { updates }, { withCredentials: true });
      setAthlete(data);
    } catch {
      toast.error("Couldn't save everything — you can finish it later from Profile.");
    } finally {
      setFinishSaving(false);
    }
  };
  // ── Done → straight into the real app ───────────────────────────
  // iter886 · No paywall after onboarding anymore — the FLYT ID live
  // link and Network Profile are free; upgrades live in the Media Hub.
  const finish = async () => {
    await saveFinalDetails();
    track('onboarding_completed', {
      has_sport: !!sport, has_link: !!linkSaved, has_photo: !!photoUrl,
      has_bio: !!bio.trim(), has_star_rating: starRating > 0,
    });
    haptic('success');
    await exitToApp();
  };
  // Tap anywhere that isn't an input/button while a text field is
  // focused → dismiss the keyboard. Fixes the "keyboard glitches, no
  // way to back out of it" complaint without needing a native
  // keyboard toolbar.
  const dismissKeyboardOnOutsideTap = (e) => {
    const tag = e.target?.tagName;
    if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'BUTTON') {
      document.activeElement?.blur?.();
    }
  };
  const exitToApp = async () => {
    onDone?.();
    // The sport pick (and every other onboarding step) only ever updated
    // the local `athlete` state here — the global user object in
    // UserAuthContext still had a stale, empty `linked_athlete.sport`.
    // Refreshing here before navigating guarantees ProfilePage always
    // mounts with the athlete record this flow just built.
    try { await refreshUser(); } catch { /* ignore — /me will catch up on the next natural poll */ }
    // iter739 · Land on the home screen after onboarding, not the profile,
    // per Jay — home is where the Launch Pad / live games / Daily Brief
    // actually show a brand-new athlete what they can build and explore,
    // instead of dropping them straight onto their (still mostly empty)
    // own profile.
    navigate('/');
  };
  const displayName = (user?.name || '').split(' ')[0] || 'you';
  return createPortal(
    <div
      className="fixed inset-0 z-[9700] flex flex-col text-white animate-flyt-onboard-in overflow-hidden"
      style={{ background: 'radial-gradient(120% 60% at 50% 0%, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0.92) 55%), #050505' }}
      data-testid="athlete-onboarding-flow"
      onClick={dismissKeyboardOnOutsideTap}
    >
      <AnimatePresence mode="wait" custom={direction}>
      <motion.div
        key={step}
        custom={direction}
        variants={slideVariants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        className="flex-1 flex flex-col min-h-0"
      >
      {step === 0 && (
        <StepShell step={0}>
          <div className="flex-1 flex flex-col min-h-0 overflow-y-auto overscroll-contain">
            <h1 className="font-display text-3xl uppercase leading-[0.95]">Add your date of birth</h1>
            <p className="mt-3 text-white/60 text-sm leading-relaxed">
              Every FLYT account needs this on file — it's what determines your eligibility for things like the 10K Earn Program and payouts.
            </p>
            <div className="mt-8">
              <label className="text-[10px] uppercase tracking-widest font-bold text-white/60 flex items-center gap-1.5 mb-2">
                <Cake className="w-3 h-3" /> Date of birth
              </label>
              <input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                required
                className="w-full px-4 py-4 rounded-2xl bg-white text-black text-sm ring-1 ring-black/10 focus:outline-none"
                style={{ color: '#000', WebkitTextFillColor: '#000' }}
                data-testid="onboarding-dob-input"
              />
            </div>
            {dobIsUnder13 && (
              <div className="mt-4 rounded-2xl bg-red-500/10 ring-1 ring-red-400/40 px-4 py-3 text-[12px] text-red-100 leading-relaxed" data-testid="onboarding-dob-under13">
                <b>FLYT accounts are currently available to athletes ages 13 and older.</b>
              </div>
            )}
          </div>
          <PrimaryButton onClick={submitDob} busy={dobSaving} disabled={!dob || dobIsUnder13} data-testid="onboarding-dob-submit">
            Submit <ArrowRight className="w-4 h-4" />
          </PrimaryButton>
          <p className="mt-3 text-[10px] text-white/40 leading-relaxed text-center" data-testid="onboarding-dob-honesty-warning">
            Please be honest — FLYT is not liable for anything that results from an inaccurate date of birth, and providing a false one violates our Terms of Service.
          </p>
        </StepShell>
      )}
      {step === 1 && (
        <StepShell step={1} onSkip={() => goTo(2)}>
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <h1 className="font-display text-3xl uppercase leading-[0.95]">Pick your sport</h1>
            <p className="mt-3 text-white/60 text-sm leading-relaxed">
              Tap yours and you're straight to the next step — you can change this later.
            </p>
            <div className="mt-6 flex-1 min-h-0 overflow-y-auto overscroll-contain -mx-1 px-1" data-testid="onboarding-sport-list">
              {SPORTS.map((s, idx) => {
                const isActive = sport === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => selectSport(s)}
                    disabled={sportSaving}
                    className={`w-full flex items-center gap-4 px-3 py-3.5 active:bg-white/[0.05] transition text-left disabled:opacity-50 ${
                      idx !== 0 ? 'border-t border-white/[0.06]' : ''
                    }`}
                    data-testid={`onboarding-sport-${s.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                  >
                    <span className="flex-1 min-w-0">
                      <span className="block font-display text-[15px] text-white leading-tight truncate">{s}</span>
                    </span>
                    {isActive && sportSaving ? (
                      <Loader2 className="w-5 h-5 animate-spin text-white/70 shrink-0" />
                    ) : isActive ? (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white shrink-0" aria-label="Selected">
                        <Check className="w-3.5 h-3.5 text-black" strokeWidth={3} />
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full ring-1 ring-white/15 shrink-0" aria-hidden="true" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </StepShell>
      )}
      {step === 2 && (
        <StepShell step={2} onBack={() => goTo(1, { back: true })} onSkip={() => goTo(3)}>
          <div className="flex-1 flex flex-col min-h-0 overflow-y-auto overscroll-contain">
            <h1 className="font-display text-3xl uppercase leading-[0.95]">Add your first link</h1>
            <p className="mt-3 text-white/60 text-sm leading-relaxed">
              TikTok, Instagram, a highlight video — paste one link and it shows up on your FLYT ID right away.
            </p>
            <input
              type="text"
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="Paste a link…"
              className="mt-8 w-full px-4 py-4 rounded-2xl bg-white text-black text-sm placeholder:text-black/40 focus:outline-none ring-1 ring-black/10"
              data-testid="onboarding-link-input"
            />
            <p className="mt-6 text-[11px] uppercase tracking-widest font-black text-white/40">
              Or tap to auto-fill (edit anytime later)
            </p>
            <div className="mt-3 flex items-center gap-3">
              {PLATFORM_ICONS.map(({ key, label, Icon, placeholder }) => {
                const isActive = !!platformLinks[key];
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => togglePlatform(key, placeholder)}
                    disabled={busy}
                    aria-label={label}
                    className={`relative w-14 h-14 rounded-2xl flex items-center justify-center transition disabled:opacity-40 ${
                      isActive ? 'bg-white text-black' : 'bg-white/[0.06] text-white ring-1 ring-white/15 hover:ring-white/30'
                    }`}
                    data-testid={`onboarding-platform-${key}`}
                  >
                    <Icon className="w-5 h-5" />
                    {isActive && (
                      <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white ring-2 ring-black flex items-center justify-center">
                        <Check className="w-3 h-3 text-black" strokeWidth={3.5} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <PrimaryButton onClick={handleLinkContinue} busy={busy} data-testid="onboarding-link-continue">
            Continue <ArrowRight className="w-4 h-4" />
          </PrimaryButton>
        </StepShell>
      )}
      {step === 3 && (
        <StepShell step={3} onBack={() => goTo(2, { back: true })} onSkip={() => goTo(4)}>
          <div className="flex-1 flex flex-col min-h-0 overflow-y-auto overscroll-contain">
            <h1 className="font-display text-3xl uppercase leading-[0.95]">Drop a quick photo</h1>
            <p className="mt-3 text-white/60 text-sm leading-relaxed">
              This one photo becomes your Media Hub upload, your profile picture, and your FLYT ID photo — all at once.
            </p>
            <label
              className="mt-8 aspect-square w-full max-w-[240px] mx-auto rounded-3xl border-2 border-dashed border-white/20 bg-white/[0.03] flex items-center justify-center cursor-pointer overflow-hidden hover:border-white/40 transition relative"
              data-testid="onboarding-photo-dropzone"
            >
              <input type="file" accept="image/*" className="hidden" onChange={handlePhotoSelect} disabled={photoUploading} />
              {photoUrl ? (
                <>
                  <img src={resolveImageUrl(photoUrl, 400)} alt="" className="w-full h-full object-cover" />
                  <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white text-black flex items-center justify-center shadow-lg">
                    <Check className="w-4 h-4" strokeWidth={3} />
                  </div>
                </>
              ) : photoUploading ? (
                <Loader2 className="w-6 h-6 animate-spin text-white/60" />
              ) : (
                <div className="flex flex-col items-center gap-2 text-white/40">
                  <ImagePlus className="w-7 h-7" />
                  <span className="text-[11px] font-bold uppercase tracking-widest">Tap to upload</span>
                </div>
              )}
            </label>
            {/* Live-ish mockup — shows where this exact photo is about to
                land, using illustrative (fake) platform pills so an
                athlete with zero real stats/links yet still sees the
                shape of a finished FLYT ID, not an empty promise. */}
            {photoUrl && (
              <div className="mt-6 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-4" data-testid="onboarding-flytid-preview">
                <p className="text-[10px] uppercase tracking-widest font-black text-white/40 mb-3">Preview · your FLYT ID</p>
                <div className="flex items-center gap-3">
                  <img src={resolveImageUrl(photoUrl, 120)} alt="" className="w-12 h-12 rounded-full object-cover ring-1 ring-white/15" />
                  <div className="min-w-0">
                    <p className="font-display text-base uppercase leading-tight truncate">{displayName}</p>
                    <p className="text-[11px] text-white/50">{sport || 'Athlete'} · FLYT Network</p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {['Hudl', 'MaxPreps', 'Opendorse'].map((p) => (
                    <span key={p} className="px-2.5 py-1 rounded-full bg-white/[0.06] ring-1 ring-white/10 text-[10px] font-bold text-white/50">{p}</span>
                  ))}
                </div>
                <p className="mt-3 text-[10px] text-white/30 leading-relaxed">
                  Sample layout — Hudl/MaxPreps/Opendorse pills appear once you connect them for real from Profile.
                </p>
              </div>
            )}
          </div>
          <PrimaryButton onClick={() => goTo(4)} busy={false} disabled={photoUploading}>
            {photoUrl ? 'Continue' : 'Skip for now'} <ArrowRight className="w-4 h-4" />
          </PrimaryButton>
        </StepShell>
      )}
      {step === 4 && (
        <StepShell step={4} onBack={() => goTo(3, { back: true })}>
          <div className="flex-1 flex flex-col min-h-0 overflow-y-auto overscroll-contain">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full ring-1 ring-white/15 bg-white/[0.04] text-[10px] uppercase tracking-widest text-white/70 font-black w-fit">
              <Sparkles className="w-3 h-3" /> You're set up
            </div>
            <h1 className="mt-4 font-display text-3xl uppercase leading-[0.95]">Your FLYT ID is live.</h1>
            <p className="mt-3 text-white/60 text-sm leading-relaxed">
              A few quick things, and your profile is already real.
            </p>
            <div className="mt-8 space-y-3">
              <RecapRow done={!!sport} label={sport ? `Sport — ${sport}` : 'Pick your sport'} />
              <RecapRow done={!!linkSaved} label="Add your first link" />
              <RecapRow done={!!photoUrl} label="Photo — Media Hub, profile picture & FLYT ID" />
            </div>
            {/* Star rating — optional, mirrors the real FLYT ID editor's
                0-5 recruit rating (`star_rating`). */}
            <div className="mt-6">
              <p className="text-[10px] uppercase tracking-widest font-black text-white/40 mb-2">Star rating (optional)</p>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => { haptic('light'); setStarRating((cur) => (cur === n ? 0 : n)); }}
                    aria-label={`${n} star${n > 1 ? 's' : ''}`}
                    data-testid={`onboarding-star-${n}`}
                  >
                    <Star className={`w-7 h-7 ${n <= starRating ? 'fill-white text-white' : 'text-white/20'}`} strokeWidth={1.5} />
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-widest font-black text-white/40">Add a short bio (optional)</p>
                {bioFocused && (
                  <button
                    type="button"
                    onClick={() => document.activeElement?.blur?.()}
                    className="text-[10px] font-black uppercase tracking-widest text-white/60 hover:text-white"
                    data-testid="onboarding-bio-done"
                  >
                    Done
                  </button>
                )}
              </div>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                onFocus={() => setBioFocused(true)}
                onBlur={() => setBioFocused(false)}
                placeholder="A sentence about you — sport, position, what drives you…"
                rows={3}
                maxLength={280}
                className="w-full px-4 py-3 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:ring-white/30 resize-none"
                data-testid="onboarding-bio-input"
              />
            </div>
          </div>
          <PrimaryButton onClick={finish} busy={finishSaving}>
            Continue <ArrowRight className="w-4 h-4" />
          </PrimaryButton>
        </StepShell>
      )}
      </motion.div>
      </AnimatePresence>
      <style>{`
        @keyframes flyt-onboard-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .animate-flyt-onboard-in {
          animation: flyt-onboard-in 200ms ease-out;
        }
      `}</style>
    </div>,
    document.body,
  );
}
const RecapRow = ({ done, label }) => (
  <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/[0.04] ring-1 ring-white/10">
    <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${done ? 'bg-white text-black' : 'bg-white/10 text-white/30'}`}>
      <Check className="w-3.5 h-3.5" strokeWidth={3} />
    </span>
    <span className={`text-sm font-bold ${done ? 'text-white' : 'text-white/40'}`}>{label}</span>
  </div>
);
